import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";

export default function TenantMessages() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [msgs, setMsgs] = useState(null);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = () =>
    api.get(`/tenant-admin/tenants/${id}/messages`).then(({ data }) => setMsgs(data)).catch(() => setMsgs([]));

  useEffect(() => {
    load();
    api.get(`/tenant-admin/tenants/${id}/profile`)
      .then(({ data }) => setName(data.name || data.company_name || ""))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tenant-admin/tenants/${id}/messages`, { text });
      setText("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (msgs === null) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  return (
    <div data-testid="tenant-messages">
      <Button variant="ghost" onClick={() => navigate("/tenants")} className="mb-4 -ml-2" data-testid="msg-back">
        <ArrowLeft className="w-4 h-4 mr-1.5" />{t("入居者一覧へ戻る")}
      </Button>
      <h1 className="font-display text-2xl font-light mb-1">{t("メッセージ")}</h1>
      {name && <div className="text-sm text-muted-foreground mb-4">{name}</div>}

      <div className="flex flex-col h-[65vh] max-w-3xl">
        <div className="flex-1 overflow-y-auto bg-white border border-border rounded-sm p-4 space-y-3" data-testid="msg-thread">
          {msgs.length === 0 && <div className="text-muted-foreground text-sm text-center py-10">{t("メッセージはまだありません。")}</div>}
          {msgs.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "staff" ? "justify-end" : "justify-start"}`} data-testid={`amsg-${m.id}`}>
              <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.sender === "staff" ? "bg-brand text-white" : "bg-gray-100 text-[#0A0A0A]"}`}>
                <div className="whitespace-pre-wrap break-words">{m.text}</div>
                <div className="text-[10px] opacity-60 mt-1">{(m.created_at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="mt-3 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("返信を入力...")}
            className="flex-1 h-11 bg-white border border-input rounded-sm px-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="amsg-input" />
          <Button type="submit" disabled={busy} className="bg-brand hover:bg-brand-hover text-white" data-testid="amsg-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      </div>
    </div>
  );
}
